import { useQueryClient } from '@tanstack/react-query'
import { isEqual } from 'lodash'
import { useForm, useWatch } from 'react-hook-form'

import {
  AddressSearchInput,
  CheckboxGroupInput,
  NetworkDisplay,
} from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { useActiveOrgId } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { useAllChainIds } from '~/hooks/utils'
import { CompliantPaymentRelation, IAddress } from '~/types'

type PaymentInfoFormValues = {
  address: IAddress
  chainIds: SupportedChainId[]
}

export function PaymentInfoDialog({
  onClose,
  payee,
}: {
  onClose: () => void
  payee: CompliantPaymentRelation
}) {
  const { control, setValue, handleSubmit } = useForm<PaymentInfoFormValues>({
    defaultValues: {
      address: payee.address,
      chainIds: payee.chains,
    },
  })
  const hasPaymentInfo =
    !!payee.address || (payee.chains && payee.chains.length > 0)

  const isEditing = hasPaymentInfo
  const { serverPutRequest, isProcessing } = useServerRequests()
  const queryClient = useQueryClient()
  const activeOrgId = useActiveOrgId()

  const onSubmit = async (data: PaymentInfoFormValues) => {
    const result = await serverPutRequest({
      path: `/api/org/compliance/payee/${payee.id}/payment-info`,
      errorMessage: 'Failed to save payment info',
      successMessage: 'Payment info saved',
      params: {
        address: data.address,
        chainIds: data.chainIds,
      },
    })
    if (result) {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.PAYEE_LOOKUP_BY_ID, activeOrgId, payee.id],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORG_COMPLIANCE, activeOrgId],
      })
      onClose()
    }
  }

  const address = useWatch({ control, name: 'address' })
  const chainIds = useWatch({ control, name: 'chainIds' })
  const allChainIds = useAllChainIds()

  const isEditingDisabled =
    (!!address && !chainIds.length) ||
    (address === payee.address && isEqual(chainIds, payee.chains))

  const isAddingDisabled = !address || chainIds.length === 0

  const isDisabled = isEditing ? isEditingDisabled : isAddingDisabled

  return (
    <SplitsDialogContent
      id="payment-info-dialog"
      title={`${isEditing ? 'Edit' : 'Add'} payment info for ${
        payee.payeeOrg?.name
      }`}
      variant="cta"
      isLoading={isProcessing}
      actionButton={{
        text: 'Save',
        onClick: handleSubmit(onSubmit),
        isDisabled,
      }}
      cancelButton={{ text: 'Cancel', onClick: onClose }}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <AddressSearchInput
          label="Address"
          control={control}
          inputName="address"
          setValue={setValue}
        />
        <CheckboxGroupInput
          label="Chains"
          tooltip="Select the chains you want to pay the recipient on"
          control={control}
          inputName="chainIds"
          options={allChainIds.map((chainId) => ({
            value: chainId,
            display: () => (
              <NetworkDisplay layout="standard" chainId={chainId} />
            ),
          }))}
        />
      </form>
    </SplitsDialogContent>
  )
}
